import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { styled, useTheme } from '@mui/material/styles';
import { Box, Stack, Drawer } from '@mui/material';
import useCollapseDrawer from '@hooks/useCollapseDrawer';
import { NAVBAR } from '~/config';
import Logo from '@components/Logo';
import InfoIcon from '~/components/InfoLink';
import LogoPlain from '~/components/LogoPlain';
import Scrollbar from '~/components/Scrollbar';
import { NavSectionVertical } from '@components/nav-section';
import navConfig from './NavConfig';
import NavbarDocs from './NavbarDocs';
import CollapseButton from './CollapseButton';

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    marginTop: '46px',
    transition: theme.transitions.create('width', {
      duration: theme.transitions.duration.shorter,
    }),
  },
}));

type Props = {
  isOpenSidebar: boolean;
  isMasquerade: boolean;
  onCloseSidebar: VoidFunction;
};

export default function NavbarVertical({ isOpenSidebar, isMasquerade, onCloseSidebar }: Props) {
  const theme = useTheme();

  const { pathname } = useRouter();

  const { isCollapse, collapseClick, onToggleCollapse } = useCollapseDrawer();

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': { height: 1, display: 'flex', flexDirection: 'column' },
      }}
    >
      <Stack
        spacing={3}
        sx={{
          pt: isCollapse ? 4 : 3,
          pb: isCollapse ? 3 : 2,
          flexShrink: 0,
          ...(isCollapse && { alignItems: 'center' }),
          ...(!isCollapse && { pr: 2, pl: 4 }),
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          {isCollapse ? <LogoPlain /> : <Logo />}

          <CollapseButton
            isCollapse={isCollapse}
            onToggleCollapse={onToggleCollapse}
            collapseClick={collapseClick}
          />
        </Stack>
      </Stack>

      <NavSectionVertical
        navConfig={navConfig}
        isCollapse={isCollapse}
        sx={{ mt: '7px', px: isCollapse ? '4px' : '16px' }}
      />

      <Box sx={{ flexGrow: isMasquerade ? 0.89 : 1 }} />

      {isCollapse ? <InfoIcon /> : <NavbarDocs />}
    </Scrollbar>
  );

  return (
    <RootStyle
      sx={{
        width: {
          lg: isCollapse ? NAVBAR.DASHBOARD_COLLAPSE_WIDTH : NAVBAR.DASHBOARD_NARROW_WIDTH,
          xl: isCollapse ? NAVBAR.DASHBOARD_COLLAPSE_WIDTH : NAVBAR.DASHBOARD_WIDTH,
        },
        ...(collapseClick && {
          position: 'absolute',
        }),
      }}
    >
      <Drawer
        open
        variant="persistent"
        PaperProps={{
          sx: {
            width: {
              lg: NAVBAR.DASHBOARD_NARROW_WIDTH,
              xl: NAVBAR.DASHBOARD_WIDTH,
            },
            bgcolor: theme.palette.primary.main,
            ...(isMasquerade && {
              top: '46px',
            }),
            transition: (theme) =>
              theme.transitions.create('width', {
                duration: theme.transitions.duration.standard,
              }),
            ...(isCollapse && {
              width: NAVBAR.DASHBOARD_COLLAPSE_WIDTH,
            }),
            overflow: 'visible',
          },
        }}
      >
        {renderContent}
      </Drawer>
    </RootStyle>
  );
}
